/**
 * Simple in-memory cache for reducing database egress
 * TTL-based cache with automatic cleanup
 */

class SimpleCache {
  constructor() {
    this.cache = new Map();
    this.timers = new Map();
  }

  /**
   * Get a cached value
   * @param {string} key - Cache key
   * @returns {any} Cached value or undefined
   */
  get(key) {
    const entry = this.cache.get(key);
    if (!entry) return undefined;

    // Check if entry has expired
    if (entry.expiresAt && Date.now() > entry.expiresAt) {
      this.delete(key);
      return undefined;
    }

    return entry.value;
  }

  /**
   * Set a cached value
   * @param {string} key - Cache key
   * @param {any} value - Value to cache
   * @param {number} ttlMs - Time to live in milliseconds (default: 60 seconds)
   */
  set(key, value, ttlMs = 60000) {
    // Clear existing timer if any
    if (this.timers.has(key)) {
      clearTimeout(this.timers.get(key));
    }

    const expiresAt = Date.now() + ttlMs;
    this.cache.set(key, { value, expiresAt });

    // Set cleanup timer
    const timer = setTimeout(() => {
      this.delete(key);
    }, ttlMs);

    this.timers.set(key, timer);
  }

  /**
   * Delete a cached value
   * @param {string} key - Cache key
   */
  delete(key) {
    this.cache.delete(key);
    if (this.timers.has(key)) {
      clearTimeout(this.timers.get(key));
      this.timers.delete(key);
    }
  }

  /**
   * Clear all cached values
   */
  clear() {
    this.timers.forEach(timer => clearTimeout(timer));
    this.cache.clear();
    this.timers.clear();
  }

  /**
   * Check if a key exists and is not expired
   * @param {string} key - Cache key
   * @returns {boolean}
   */
  has(key) {
    return this.get(key) !== undefined;
  }

  /**
   * Get or set a cached value
   * If the key exists, returns the cached value
   * Otherwise, calls the factory function, caches the result, and returns it
   * @param {string} key - Cache key
   * @param {Function} factory - Async function to generate the value if not cached
   * @param {number} ttlMs - Time to live in milliseconds
   * @returns {Promise<any>}
   */
  async getOrSet(key, factory, ttlMs = 60000) {
    const cached = this.get(key);
    if (cached !== undefined) {
      return cached;
    }

    const value = await factory();
    this.set(key, value, ttlMs);
    return value;
  }
}

// Create singleton instances for different cache types
export const competitionsCache = new SimpleCache();
export const winnersCache = new SimpleCache();
export const categoryCountsCache = new SimpleCache();

// Cache TTL constants (in milliseconds)
export const CACHE_TTL = {
  COMPETITIONS_LIST: 30000,     // 30 seconds for competition list
  WINNERS_LIST: 300000,         // 5 minutes for past winners
  CATEGORY_COUNTS: 60000,       // 1 minute for category counts
  PAGE_METADATA: 600000,        // 10 minutes for page metadata
};

export default SimpleCache;
